import _ from 'lodash'
import type {
	CompGridData,
	MeshComponent,
	Dock,
	DockValue,
	GridOverflow,
	GridVariant,
	MeshDataOptions,
	MeshMarginsOptions,
	MeshStyle,
	VerticalArea,
} from '@wix/thunderbolt-becky-types'

import { bottom, fitToContentHeight, left, top } from './compLayoutUtils'
import { getComponentBoundingBox } from './boundingLayout'

const isIE = (cssGridVariant: GridVariant) => cssGridVariant === 'ms'

export const unitize = (n: number | string) => (_.isString(n) ? n : `${n}px`)

export const getRootContainerStyle = (container: MeshComponent): MeshStyle => ({
	height: 'auto',
	width: container.width ? unitize(container.width) : '',
})

const overflowDirectionToMarginKey: { top: 'marginTop'; bottom: 'marginBottom' } = {
	top: 'marginTop',
	bottom: 'marginBottom',
}

const getOverflowGridMargins = (gridOverflow: VerticalArea): MeshStyle =>
	_(gridOverflow)
		.pickBy((distance: number) => distance > 0)
		// @ts-ignore
		.reduce((result: MeshStyle, distance, direction: 'top' | 'bottom') => {
			// @ts-ignore
			result[overflowDirectionToMarginKey[direction]] = unitize(distance * -1)

			return result
		}, {})

const getTemplateRepeat = (numberOfRows: number, isRepeatSupported: boolean): Array<string> => {
	if (!isRepeatSupported || numberOfRows < 2) {
		return _.times(numberOfRows, _.constant('min-content'))
	}

	return [`repeat(${numberOfRows}, min-content)`]
}

const templateRows = (numberOfRows: number, ie: boolean, isRepeatSupported: boolean): MeshStyle => ({
	[ie ? '-ms-grid-rows' : 'gridTemplateRows']: getTemplateRepeat(numberOfRows - 1, isRepeatSupported)
		.concat('1fr')
		.join(' '),
})

export const getGridWrapperStyle = (gridOverflow: GridOverflow, ie: boolean): MeshStyle =>
	gridOverflow || ie ? { display: ie ? '-ms-flexbox' : 'flex' } : {}
const getFitToContentPaddingStyle = (_fitToContentHeight: boolean, fitToContentPadding: string | number): MeshStyle => {
	if (_fitToContentHeight && (_.isNumber(fitToContentPadding) || fitToContentPadding)) {
		return {
			paddingBottom: unitize(fitToContentPadding),
			boxSizing: 'border-box',
		}
	}

	return {}
}

export const getGridContainerStyles = (
	cssGridVariant: GridVariant,
	numberOfRows: number,
	gridOverflow: VerticalArea,
	containerMinHeight: string,
	_fitToContentHeight: boolean,
	fitToContentPadding: number | string,
	isRepeatSupported: boolean
): MeshStyle => {
	const ie = isIE(cssGridVariant)

	return {
		position: 'static',
		display: ie ? '-ms-grid' : 'grid',
		height: 'auto',
		width: '100%',
		minHeight: containerMinHeight,
		...getOverflowGridMargins(gridOverflow),
		...templateRows(numberOfRows, ie, isRepeatSupported),
		...{ [ie ? '-ms-grid-columns' : 'gridTemplateColumns']: '100%' },
		...getFitToContentPaddingStyle(_fitToContentHeight, fitToContentPadding),
	}
}

export const getEmptyContainerStyles = (
	containerMinHeight: string,
	_fitToContentHeight: boolean,
	fitToContentPadding: number | string
): MeshStyle => ({
	position: 'static',
	height: 'auto',
	width: '100%',
	minHeight: containerMinHeight,
	...getFitToContentPaddingStyle(_fitToContentHeight, fitToContentPadding),
})

const getGridArea = (rowStart: number, columnStart: number, rowEnd: number, columnEnd: number, ie: boolean) =>
	ie
		? {
				'-ms-grid-column': columnStart.toString(),
				'-ms-grid-column-span': (columnEnd - columnStart).toString(),
				'-ms-grid-row': rowStart.toString(),
				'-ms-grid-row-span': (rowEnd - rowStart).toString(),
		  }
		: {
				gridArea: [rowStart, columnStart, rowEnd, columnEnd].join(' / '),
		  }

const getMarginBottom = (
	comp: MeshComponent,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	container: MeshComponent,
	{ softMargin, minLockMargin }: MeshMarginsOptions,
	// @ts-ignore
	negativeBottomTops
): string => {
	if (compGridData.bottomParent && !fitToContentHeight(container)) {
		const distanceFromRowBottom = gridVerticalArea.bottom - bottom(comp)
		// @ts-ignore
		return unitize(distanceFromRowBottom > minLockMargin ? softMargin : distanceFromRowBottom)
	}

	// @ts-ignore
	if (negativeBottomTops[comp.id]) {
		// @ts-ignore
		return unitize(-1 * negativeBottomTops[comp.id])
	}

	return compGridData.marginBottom ? unitize(compGridData.marginBottom) : '0'
}

const dockedToMargin = (margin?: DockValue): string => {
	if (!margin) {
		// @ts-ignore
		return null
	}

	const vals = _.compact([
		margin.px && `${margin.px}px`,
		margin.pct && `${margin.pct}%`,
		margin.vw && `${margin.vw}vw`,
	])

	switch (_.size(vals)) {
		case 0:
			return '0'
		case 1:
			return vals[0]
		default:
			return `calc(${_.join(vals, ' + ')})`
	}
}

const getMarginLeft = (comp: MeshComponent, container: MeshComponent): string =>
	dockedToMargin(comp.docked && comp.docked.left) ||
	(container.contentArea
		? `calc((100% - ${container.contentArea.width}px) * ${container.contentArea.alignment})`
		: '0')

const getMarginRight = (comp: MeshComponent): string => dockedToMargin(comp.docked && comp.docked.right) || '0px'

const getJustify = (dock?: Dock): 'start' | 'end' | 'stretch' | 'center' => {
	const hdock = _.pick(dock, ['left', 'right']) || {}

	if (hdock.left && hdock.right) {
		return 'stretch'
	}

	if (hdock.right) {
		return 'end'
	}

	return 'start'
}

export const getWedgeStyle = (wedgeGridData: CompGridData, ie: boolean): MeshStyle => ({
	visibility: 'hidden',
	// @ts-ignore
	height: unitize(wedgeGridData.height),
	width: '0',
	...getGridArea(wedgeGridData.rowStart, 1, wedgeGridData.rowEnd, 2, ie),
})

const getCompMargins = (
	comp: MeshComponent,
	compGridData: CompGridData,
	container: MeshComponent,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshMarginsOptions,
	// @ts-ignore
	negativeBottomTops
): string => {
	// @ts-ignore
	const _top = unitize(compGridData.marginTop)
	const _right = getMarginRight(comp)
	const _bottom = getMarginBottom(comp, compGridData, gridVerticalArea, container, meshOptions, negativeBottomTops)
	const _left = getMarginLeft(comp, container)

	return `${_top} ${_right} ${_bottom} ${_left}`
}

export const getCompStyles = (
	comp: MeshComponent,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	container: MeshComponent,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
): MeshStyle => {
	// @ts-ignore
	const ie = isIE(meshOptions.cssGridVariant)

	return {
		position: 'relative',
		margin: getCompMargins(comp, compGridData, container, gridVerticalArea, meshOptions, negativeBottomTops),
		left: _.has(comp, 'docked.left') ? 0 : unitize(left(comp)),
		...getGridArea(compGridData.rowStart, 1, compGridData.rowEnd, 2, ie),
		[ie ? '-ms-grid-column-align' : 'justifySelf']: getJustify(comp.docked),
		[ie ? '-ms-grid-row-align' : 'alignSelf']: 'start',
	}
}

export const getRotatedComponentWrapperStyle = (
	comp: MeshComponent,
	container: MeshComponent,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
): MeshStyle => {
	// @ts-ignore
	const ie = isIE(meshOptions.cssGridVariant)
	const wrapperComp = _.assign({}, comp, getComponentBoundingBox(comp))

	return {
		position: 'static',
		height: unitize(Math.ceil(wrapperComp.height)),
		width: '0',
		margin: getCompMargins(wrapperComp, compGridData, container, gridVerticalArea, meshOptions, negativeBottomTops),
		...getGridArea(compGridData.rowStart, 1, compGridData.rowEnd, 2, ie),
		[ie ? '-ms-grid-column-align' : 'justifySelf']: getJustify(wrapperComp.docked),
		[ie ? '-ms-grid-row-align' : 'alignSelf']: 'start',
	}
}

export const getRotatedComponentStyle = (comp: MeshComponent): MeshStyle => {
	const childBoundingBox = getComponentBoundingBox(comp)
	return {
		position: 'relative',
		left: _.has(comp, 'docked.left') ? '0' : unitize(left(comp)),
		top: unitize(top(comp) - childBoundingBox.top),
	}
}

export const getCompAbsoluteStyle = (comp: MeshComponent, container: MeshComponent): MeshStyle => {
	return {
		position: 'absolute',
		// @ts-ignore
		top: unitize(comp.top),
		// @ts-ignore
		left: unitize(comp.left),
		marginLeft: getMarginLeft(comp, container),
	}
}
